import { Box, Collapse, IconButton, Typography, useTheme } from '@mui/material'
import { useState } from 'react'

import { ChevronDownIcon } from 'src/assets/icons/v2/ChevronDownIcon'
import { ChevronUpIcon } from 'src/assets/icons/v2/ChevronUpIcon'

import { AssociationsListItem, AssociationsListWrapper, Loader } from 'src/components'
import { FONTS } from 'src/constants'

import {
  IProductByIdRawRespDto,
  IProductsAllRawRespDto,
  IScanAccountImpact,
  IScanAccountImpactsRespDto,
} from 'src/types'

interface IProps<T, U> {
  label: string
  notFoundLable: string
  selectedItem: string
  handleSelectItem: (item: T) => void
  items: void | U | undefined
  isLoading: boolean
  isOpened: boolean
  isOpenedByDefault?: boolean
}

export const ExpandableList = <
  T extends IProductByIdRawRespDto | IScanAccountImpact,
  U extends IProductsAllRawRespDto | IScanAccountImpactsRespDto,
>({
  selectedItem,
  handleSelectItem,
  items,
  isLoading,
  label,
  notFoundLable,
  isOpened,
  isOpenedByDefault,
}: IProps<T, U>) => {
  const [toggle, setToggle] = useState(isOpenedByDefault ?? isOpened)
  const theme = useTheme()

  const handleItemClick = (item: T) => () => {
    handleSelectItem(item)
  }

  const handleCollapse = () => {
    setToggle((toggle) => !toggle)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <Box
        onClick={handleCollapse}
        sx={{
          width: 'fit-content',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minWidth: '95px',
          gap: '5px',
          cursor: 'pointer',
        }}
      >
        <Typography
          sx={{
            textAlign: 'center',
            color: theme.palette.custom.grey3,
            fontSize: FONTS.SIZES.s,
            fontWeight: FONTS.WEIGHTS.bold,
          }}
        >
          {label}
        </Typography>
        <IconButton>
          {toggle ? (
            <ChevronUpIcon size={24} color={theme.palette.custom.grey3} />
          ) : (
            <ChevronDownIcon size={24} color={theme.palette.custom.grey3} />
          )}
        </IconButton>
      </Box>
      <Collapse in={toggle} timeout="auto" unmountOnExit>
        {isLoading ? (
          <Loader />
        ) : (
          <AssociationsListWrapper>
            {items?.data
              .sort((a, b) => {
                const aIsActive = selectedItem === a.id
                const bIsActive = selectedItem === b.id
                if (aIsActive && !bIsActive) {
                  return -1
                }
                if (bIsActive && !aIsActive) {
                  return 1
                }
                return 0
              })
              .map((props) => {
                return (
                  <AssociationsListItem
                    key={props.id}
                    active={selectedItem === props.id}
                    handleClick={handleItemClick(props as T)}
                    image={props.image_small || props.image}
                    title={props.name}
                    subtitle={'#' + props.code}
                  />
                )
              })}
            {items?.count === 0 && <Typography fontSize={14}>{notFoundLable}</Typography>}
          </AssociationsListWrapper>
        )}
      </Collapse>
    </Box>
  )
}
